import React, { useState, useEffect, ChangeEvent } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { IFactor } from 'types/carrier'
import {
  closeCarrierAccountingModal,
  updateCarrierAccounting,
} from 'apps/carrier/utils/actions/carrierAccounting'
import { IStore } from 'apps/carrier/utils/reducers'
import { TextInput } from 'components/atoms'
import { FormModal } from 'transfix-ui'
import { ISelector } from './Accounting'
import { FactoringAddress } from './FactoringAddress'

interface IFormState {
  factored?: boolean
  factor?: IFactor
  carrierBillingLocationAddress?: string
  carrierBillingLocationAddressLine2?: string
  carrierBillingLocationCity?: string
  carrierBillingLocationStateCode?: string
  carrierBillingLocationZipcode?: string
  carrierBillingLocationCountryCode?: string
  carrierBillingLocationContactEmail?: string
  carrierBillingLocationContactPhone?: string
  hasCarrierBillingLocation?: boolean
}

interface IValidationErrors {
  accountingEmail?: string
  accountingPhoneNumber?: string
  factor?: string
  carrierBillingLocation?: string
}

interface IValidation {
  pristine: boolean
  errors: IValidationErrors
}

export const AccountingModal = () => {
  const dispatch = useDispatch()
  const { accounting, carrierId, error, showModal, updating } = useSelector<IStore, ISelector>(
    store => ({ ...store.accounting, carrierId: store.carrier.carrierDetails?.id })
  )
  const [formState, setState] = useState<IFormState>({})
  const [validation, setValidation] = useState<IValidation>({ pristine: true, errors: {} })
  useEffect(() => {
    if (showModal) {
      setState({
        factored: Boolean(accounting!.factor),
        hasCarrierBillingLocation: Boolean(accounting!.carrierBillingLocation),
        factor: accounting!.factor?.id ? accounting!.factor : undefined,
        carrierBillingLocationAddress: accounting!.carrierBillingLocation?.id
          ? accounting!.carrierBillingLocation.address
          : '',
        carrierBillingLocationCity: accounting!.carrierBillingLocation?.id
          ? accounting!.carrierBillingLocation?.city
          : '',
        carrierBillingLocationStateCode: accounting!.carrierBillingLocation?.id
          ? accounting!.carrierBillingLocation?.stateCode
          : '',
        carrierBillingLocationCountryCode: accounting!.carrierBillingLocation?.id
          ? accounting!.carrierBillingLocation?.countryCode
          : '',
        carrierBillingLocationZipcode: accounting!.carrierBillingLocation?.id
          ? accounting!.carrierBillingLocation?.zipcode
          : '',
        carrierBillingLocationContactEmail: accounting!.carrierBillingLocation?.id
          ? accounting!.carrierBillingLocation?.contactEmail
          : '',
        carrierBillingLocationContactPhone: accounting!.carrierBillingLocation?.id
          ? accounting!.carrierBillingLocation?.contactPhone
          : '',
      })
      setValidation({ pristine: true, errors: {} })
    }
  }, [showModal, accounting])

  const validate = (nextState: IFormState) => {
    const errors: IValidationErrors = {}

    if (nextState.factored && !nextState.factor?.id) {
      errors.factor = 'Factoring Company is not set'
    }

    setValidation({ pristine: false, errors })
    return !Object.keys(errors).length
  }

  const setFormState = (change: {}) =>
    setState(current => {
      const nextState = { ...current, ...change }
      if (!validation.pristine) {
        validate(nextState)
      }
      return nextState
    })

  const onCarrierBillingLocationChangeHandler = ({
    target: { id, value },
  }: ChangeEvent<HTMLInputElement>) => setFormState({ [id]: value })

  const closeDialog = (action?: string) => {
    if (action === 'confirm') {
      if (validate(formState)) {
        const factorId = formState.factored && formState.factor?.id

        dispatch(
          updateCarrierAccounting(carrierId!, {
            factorId: factorId || null,
            carrierBillingLocation: {
              address: formState.carrierBillingLocationAddress,
              addressLine2: formState.carrierBillingLocationAddressLine2,
              city: formState.carrierBillingLocationCity,
              stateCode: formState.carrierBillingLocationStateCode,
              countryCode: formState.carrierBillingLocationCountryCode,
              zipcode: formState.carrierBillingLocationZipcode,
              contactPhone: formState.carrierBillingLocationContactPhone,
              contactEmail: formState.carrierBillingLocationContactEmail,
            },
          })
        )
      }
    } else {
      dispatch(closeCarrierAccountingModal())
    }
  }

  return (
    <FormModal
      title='Edit Accounting Details'
      size={4}
      open={showModal}
      onClose={closeDialog}
      actions={[
        {
          label: 'Cancel',
          type: 'dismiss',
          disabled: updating,
          dataTest: 'accounting-cancel',
        },
        {
          label: 'Save',
          type: 'confirm',
          disabled: updating,
          dataTest: 'accounting-save',
        },
      ]}
      error={error}>
      <div className='row'>
        <div className='col-md-6 col-xs-12'>
          <TextInput
            id='carrierBillingLocationAddress'
            placeholder=''
            label='Carrier Billing Address'
            value={formState.carrierBillingLocationAddress || ''}
            onChange={onCarrierBillingLocationChangeHandler}
            dataTest='accounting-carrier-billing-location-address'
            invalid={Boolean(validation.errors.carrierBillingLocation)}
            validationMessage={validation.errors.carrierBillingLocation}
          />
        </div>
      </div>
      <div className='row'>
        <div className='col-md-6 col-xs-12'>
          <TextInput
            id='carrierBillingLocationAddressLine2'
            placeholder=''
            label='Carrier Billing Address Line 2'
            value={formState.carrierBillingLocationAddressLine2 || ''}
            onChange={onCarrierBillingLocationChangeHandler}
            dataTest='accounting-carrier-billing-location-address-line-2'
            invalid={Boolean(validation.errors.carrierBillingLocation)}
            validationMessage={validation.errors.carrierBillingLocation}
          />
        </div>
      </div>
      <div className='row'>
        <div className='col-md-6 col-xs-12'>
          <TextInput
            id='carrierBillingLocationCity'
            placeholder=''
            label='Carrier Billing City'
            value={formState.carrierBillingLocationCity || ''}
            onChange={onCarrierBillingLocationChangeHandler}
            dataTest='accounting-carrier-billing-location-city'
            invalid={Boolean(validation.errors.carrierBillingLocation)}
            validationMessage={validation.errors.carrierBillingLocation}
          />
        </div>
      </div>
      <div className='row'>
        <div className='col-md-6 col-xs-12'>
          <TextInput
            id='carrierBillingLocationStateCode'
            placeholder=''
            label='Carrier Billing State Code'
            value={formState.carrierBillingLocationStateCode || ''}
            onChange={onCarrierBillingLocationChangeHandler}
            dataTest='accounting-carrier-billing-location-state-code'
            invalid={Boolean(validation.errors.carrierBillingLocation)}
            validationMessage={validation.errors.carrierBillingLocation}
          />
        </div>
      </div>
      <div className='row'>
        <div className='col-md-6 col-xs-12'>
          <TextInput
            id='carrierBillingLocationZipcode'
            placeholder=''
            label='Carrier Billing Zipcode'
            value={formState.carrierBillingLocationZipcode || ''}
            onChange={onCarrierBillingLocationChangeHandler}
            dataTest='accounting-carrier-billing-location-zipcode'
            invalid={Boolean(validation.errors.carrierBillingLocation)}
            validationMessage={validation.errors.carrierBillingLocation}
          />
        </div>
        <div className='row'>
          <div className='col-md-6 col-xs-12'>
            <TextInput
              id='carrierBillingLocationCountryCode'
              placeholder=''
              label='Carrier Billing Country Code'
              value={formState.carrierBillingLocationCountryCode || ''}
              onChange={onCarrierBillingLocationChangeHandler}
              dataTest='accounting-carrier-billing-location-country-code'
              invalid={Boolean(validation.errors.carrierBillingLocation)}
              validationMessage={validation.errors.carrierBillingLocation}
            />
          </div>
        </div>
        <div className='row'>
          <div className='col-md-6 col-xs-12'>
            <TextInput
              id='carrierBillingLocationContactPhone'
              placeholder=''
              label='Carrier Billing Contact Phone'
              value={formState.carrierBillingLocationContactPhone || ''}
              onChange={onCarrierBillingLocationChangeHandler}
              dataTest='accounting-carrier-billing-location-contact-phone'
              invalid={Boolean(validation.errors.carrierBillingLocation)}
              validationMessage={validation.errors.carrierBillingLocation}
            />
          </div>
        </div>
        <div className='row'>
          <div className='col-md-6 col-xs-12'>
            <TextInput
              id='carrierBillingLocationContactEmail'
              placeholder=''
              label='Carrier Billing Contact Email'
              value={formState.carrierBillingLocationContactEmail || ''}
              onChange={onCarrierBillingLocationChangeHandler}
              dataTest='accounting-carrier-billing-location-contact-email'
              invalid={Boolean(validation.errors.carrierBillingLocation)}
              validationMessage={validation.errors.carrierBillingLocation}
            />
          </div>
        </div>
      </div>
      {formState.factored && (
        <>
          <div className='row'>
            <div className='col-xs-12'>
              <FactoringAddress factor={formState.factor} dialogMode />
            </div>
          </div>
        </>
      )}
    </FormModal>
  )
}
